import { useState, useEffect } from 'react'
import { Box } from '@mui/material'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { ErrorBoundary } from 'Features/Error/ErrorBoundary'
import { Footer } from 'Common/Supplyvue-ui'
import { Breadcrumbs } from 'Common/Breadcrumbs/Breadcrumbs'
import { SubCrumbProvider } from 'Common/Breadcrumbs/SubCrumb.context'
import { BrandBar } from './BrandBar'

// Top-level pages have nothing above them to go back to, so the crumb bar
// would only say "Projects".
const NO_CONTEXT_BAR = ['/', '/projects', '/tenants', '/select-tenant']

/**
 * The shared SupplyVue shell (design system: The shared shell): brand bar,
 * crumb bar, the content column, footer. Same frame as the Planner Portal;
 * only the product word and mark differ.
 *
 * A fixed-height flex column where only the content column scrolls, so a grid
 * inside it can flex to fill instead of having a pixel height written onto it.
 */
export const Shell = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const tenantName = localStorage.getItem('tenantName') ?? ''

  useEffect(() => {
    // Check if we're in an auth redirect flow to prevent unnecessary redirects
    const isAuthRedirect =
      sessionStorage.getItem('authRedirectInProgress') === 'true'

    if (!tenantName && !isAuthRedirect && location.pathname !== '/tenants') {
      navigate('/tenants', { replace: true })
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    // Don't clear localStorage if we're in an auth redirect flow
    const isAuthRedirect =
      sessionStorage.getItem('authRedirectInProgress') === 'true'

    if (window.location.pathname === '/tenants' && !isAuthRedirect) {
      localStorage.removeItem('tenantId')
      localStorage.removeItem('tenantsCount')
      localStorage.removeItem('tenantName')
    }
  }, [])

  if (loading) {
    return null
  }

  const showContextBar = !NO_CONTEXT_BAR.includes(location.pathname)

  return (
    <SubCrumbProvider>
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <BrandBar product="Modelling" />

        {showContextBar && (
          <Box
            sx={{
              minHeight: 'var(--crumb-bar-h)',
              flexShrink: 0,
              background: 'var(--surface-0)',
              borderBottom: '1px solid var(--line-300)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              px: '12px',
            }}
          >
            <Box
              component="button"
              type="button"
              title="Back"
              aria-label="Back"
              onClick={() => navigate(-1)}
              sx={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                p: '4px',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--ink-500)',
                flexShrink: 0,
                '&:hover': { background: 'rgba(0,65,90,0.06)' },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
            </Box>
            <Breadcrumbs />
          </Box>
        )}

        <ErrorBoundary>
          <Box
            component="main"
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'var(--surface-50)',
            }}
          >
            <Outlet />
          </Box>
        </ErrorBoundary>

        <Footer
          copyrightText="All rights reserved"
          privacyText="Privacy Notice"
          privacyHref="https://www.supplyvue.com/privacy-notice/"
          cookiesText="Cookies"
          cookiesHref="https://www.supplyvue.com/cookies/"
        />
      </Box>
    </SubCrumbProvider>
  )
}
